import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import {
  selectIsPackageSearcherEnabled,
  selectIsFlightSearcherEnabled,
  selectIsHotelSearcherEnabled,
} from 'legacyfrontend/redux/selectors/application-selectors';
import PackageSearch from './PackageSearch';
import FlightsSearch from './FlightsSearch';
import HotelsSearch from './HotelsSearch';
import MobileSearchDialog from './MobileSearchDialog';
import MobileSearchButton from './MobileSearchButton';
import BannersComponents from 'legacyfrontend/components/home/BannersComponents';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import FeatureSelector from './FeatureSelector';
import { selectHasActiveSearchers } from 'legacyfrontend/redux/selectors/application-selectors';

const { BUSCAR_X_PAQUETES, BUSCAR_X_VUELOS, BUSCAR_X_HOTELES } = ROUTE_KEYS;

const SearchSelector = ({ isOnTargetPath }) => {
  const { pathname, search } = useLocation();
  const { background, text } = useColors();
  const { onSearchCount } = useGlobal();

  const [isMobile, setIsMobile] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [showMobileDialog, setShowMobileDialog] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [hasAutoExecuted, setHasAutoExecuted] = useState(false);

  const packageSearchRef = useRef(null);
  const flightsSearchRef = useRef(null);
  const hotelsSearchRef = useRef(null);

  const isPackageSearcherEnabled = useSelector(selectIsPackageSearcherEnabled);
  const isFlightSearcherEnabled = useSelector(selectIsFlightSearcherEnabled);
  const isHotelSearcherEnabled = useSelector(selectIsHotelSearcherEnabled);

  const hasAnySearcher = useSelector(selectHasActiveSearchers);

  const normalizedPathname = pathname.replace(/\/$/, '');
  const shouldShowPackageSearch =
    normalizedPathname === BUSCAR_X_PAQUETES && isPackageSearcherEnabled;
  const shouldShowFlightsSearch = normalizedPathname === BUSCAR_X_VUELOS && isFlightSearcherEnabled;
  const shouldShowHotelsSearch = normalizedPathname === BUSCAR_X_HOTELES;
  const shouldShowBanners = onSearchCount === 0;

  const buttonText = shouldShowPackageSearch
    ? 'Buscar Paquetes'
    : shouldShowFlightsSearch
      ? 'Buscar Vuelos'
      : 'Buscar Hoteles';

  const hasUrlParams = useMemo(() => {
    if (isClient && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      return params.toString().length > 0;
    }
    return false;
  }, [search]);

  useEffect(() => {
    const checkTablet = () => {
      const newIsMobile = window.innerWidth <= 1024;
      setIsMobile(prevIsMobile => {
        if (newIsMobile !== prevIsMobile) {
          return newIsMobile;
        }
        return prevIsMobile;
      });
    };

    if (isClient) {
      checkTablet();
      window.addEventListener('resize', checkTablet);
      return () => window.removeEventListener('resize', checkTablet);
    }
  }, []);

  useEffect(() => {
    if (hasUrlParams && isClient && !isMobile && !hasAutoExecuted) {
      setHasAutoExecuted(true);
    }
  }, [hasUrlParams, isClient, isMobile, hasAutoExecuted]);

  const handleMobileSearchClick = (type) => {
    setDialogType(type);
    setShowMobileDialog(true);
  };

  const handleCloseMobileDialog = () => {
    setShowMobileDialog(false);
    setDialogType(null);
    setIsSearchLoading(false);
  };

  useEffect(() => {
    if (!showMobileDialog) {
      setIsSearchLoading(false);
    }
  }, [showMobileDialog]);

  const handleSearchStart = () => {
    setIsSearchLoading(true);
  };

  const handleSearchComplete = () => {
    if (isSearchLoading) {
      setIsSearchLoading(false);
      setShowMobileDialog(false);
      setDialogType(null);
    }
  };

  const handleMobileSearch = () => {
    try {
      if (
        dialogType === 'packages' &&
        packageSearchRef.current &&
        packageSearchRef.current.triggerSearch
      ) {
        packageSearchRef.current.triggerSearch();
      } else if (
        dialogType === 'flights' &&
        flightsSearchRef.current &&
        flightsSearchRef.current.triggerSearch
      ) {
        flightsSearchRef.current.triggerSearch();
      } else if (
        dialogType === 'hotels' &&
        hotelsSearchRef.current &&
        hotelsSearchRef.current.triggerSearch
      ) {
        hotelsSearchRef.current.triggerSearch();
      }
    } catch (error) {
    }
  };

  const updateFormValidation = useCallback(() => {
    try {
      if (
        dialogType === 'packages' &&
        packageSearchRef.current &&
        packageSearchRef.current.isFormValid
      ) {
        setIsValidForm(packageSearchRef.current.isFormValid());
      } else if (
        dialogType === 'flights' &&
        flightsSearchRef.current &&
        flightsSearchRef.current.isFormValid
      ) {
        setIsValidForm(flightsSearchRef.current.isFormValid());
      } else if (
        dialogType === 'hotels' &&
        hotelsSearchRef.current &&
        hotelsSearchRef.current.isFormValid
      ) {
        setIsValidForm(hotelsSearchRef.current.isFormValid());
      } else {
        setIsValidForm(false);
      }
    } catch (error) {
      setIsValidForm(false);
    }
  }, [dialogType]);

  useEffect(() => {
    if (showMobileDialog && dialogType) {
      const interval = setInterval(updateFormValidation, 500);
      return () => clearInterval(interval);
    }
  }, [showMobileDialog, dialogType, updateFormValidation]);


  const shouldShowImage =
    normalizedPathname === BUSCAR_X_PAQUETES || normalizedPathname === BUSCAR_X_VUELOS || normalizedPathname === BUSCAR_X_HOTELES;

  if (!shouldShowImage) {
    return null;
  }

  if (isMobile) {
    return (
      <>
        <div className='stacked__container'>
          <div className='absolute bottom-0 w-full flex justify-center px-2 -mt-4 z-[1] shadow-lg'>
            {hasAnySearcher && (
              <div className='header__package-search__container'>
                <MobileSearchButton
                  onClick={() =>
                    handleMobileSearchClick(
                      shouldShowPackageSearch
                        ? 'packages'
                        : shouldShowFlightsSearch
                          ? 'flights'
                          : 'hotels'
                    )
                  }
                  text={buttonText}
                  backgroundColor={background}
                  textColor={text}
                />
              </div>
            )}
          </div>
        </div>

        {showMobileDialog && dialogType && (
          <MobileSearchDialog
            open={showMobileDialog}
            onClose={handleCloseMobileDialog}
            title={
              dialogType === 'packages'
                ? 'Buscar Paquetes'
                : dialogType === 'flights'
                  ? 'Buscar Vuelos'
                  : 'Buscar Hoteles'
            }
            background={background}
            textColor={text}
            isLoading={isSearchLoading}
            onSearch={handleMobileSearch}
            searchButtonText='Buscar'
            isSearchDisabled={!isValidForm}
          >
            {dialogType === 'packages' && shouldShowPackageSearch ? (
              <PackageSearch
                ref={packageSearchRef}
                isOnTargetPath={isOnTargetPath}
                background={background}
                text={text}
                onCloseMobileSearch={handleCloseMobileDialog}
                onSearchStart={handleSearchStart}
                onSearchComplete={handleSearchComplete}
                isMobile={true}
                isLoading={isSearchLoading}
              />
            ) : dialogType === 'flights' && shouldShowFlightsSearch ? (
              <FlightsSearch
                ref={flightsSearchRef}
                isOnTargetPath={isOnTargetPath}
                background={background}
                text={text}
                onCloseMobileSearch={handleCloseMobileDialog}
                onSearchStart={handleSearchStart}
                onSearchComplete={handleSearchComplete}
                isMobile={true}
                isLoading={isSearchLoading}
              />
            ) : dialogType === 'hotels' && shouldShowHotelsSearch ? (
              <HotelsSearch
                ref={hotelsSearchRef}
                isOnTargetPath={isOnTargetPath}
                background={background}
                text={text}
                onCloseMobileSearch={handleCloseMobileDialog}
                onSearchStart={handleSearchStart}
                onSearchComplete={handleSearchComplete}
                isMobile={true}
                isLoading={isSearchLoading}
              />
            ) : null}
          </MobileSearchDialog>
        )}
        {shouldShowBanners && <BannersComponents />}

      </>
    );
  }

  return (
    <>
      <div className='stacked__container'>
        <div className={`absolute w-full bottom-[2rem] flex justify-center flex-col z-10 ${shouldShowHotelsSearch
          ? 'px-[10vw] sm:px-[8vw] xl:px-[10vw] 2xl:px-[15vw]'
          : 'px-[15vw] sm:px-[10vw] xl:px-[18vw] 2xl:px-[23vw]'
          }`}>
          <div className='mb-2'>
            <FeatureSelector />
          </div>
          {shouldShowPackageSearch && (
            <div className='header__package-search__container'>
              <PackageSearch
                ref={packageSearchRef}
                isOnTargetPath={isOnTargetPath}
                background={background}
                text={text}
                onCloseMobileSearch={() => { }}
                isMobile={false}
              />
            </div>
          )}
          {shouldShowFlightsSearch && (
            <div className='header__package-search__container'>
              <FlightsSearch
                ref={flightsSearchRef}
                isOnTargetPath={isOnTargetPath}
                background={background}
                text={text}
                onCloseMobileSearch={() => { }}
                isMobile={false}
              />
            </div>
          )}
          {shouldShowHotelsSearch && (
            <div className='header__package-search__container'>
              <HotelsSearch
                ref={hotelsSearchRef}
                isOnTargetPath={isOnTargetPath}
                background={background}
                text={text}
                onCloseMobileSearch={() => { }}
                isMobile={false}
              />
            </div>
          )}
        </div>
      </div>

      {shouldShowBanners && <BannersComponents />}

    </>
  );
};

export default SearchSelector;
